import { useEffect, useRef, type PropsWithChildren } from 'react';

import { ErrorBoundary } from '@datadog/browser-rum-react';
import { SplitFactoryProvider } from '@splitsoftware/splitio-react';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { ThemeProvider } from '@mui/material/styles';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { Outlet, useLocation } from 'react-router';

import ErrorPage from '@/features/Home/ErrorPage';
import getEnv from '@/shared/configs/env';
import { queryClient } from '@/shared/libs';
import { AnalyticsProvider } from '@/shared/providers/analytics';
import { splitConfig } from '@/shared/providers/feature-flags';
import { InfiniteWatchProvider } from '@/shared/providers/InfiniteWatchScript';
import { materialTheme } from '@/shared/styles';
import { useUrlChecker } from '@/routing/useUrlChecker';

export const ApplicationProviders = ({ children }: PropsWithChildren) => (
  <SplitFactoryProvider config={splitConfig}>
    <InfiniteWatchProvider />
    <ThemeProvider theme={materialTheme}>
      <QueryClientProvider client={queryClient}>
        {children}
        <ReactQueryDevtools initialIsOpen={false} buttonPosition="bottom-right" />
      </QueryClientProvider>
    </ThemeProvider>
  </SplitFactoryProvider>
);

const RouteErrorFallback = ({ resetError }: { error: Error; resetError: () => void }) => {
  const { pathname } = useLocation();
  const errorPathRef = useRef(pathname);

  // Recover when the user leaves the failing route (button, back, or any other navigation).
  useEffect(() => {
    if (pathname !== errorPathRef.current) {
      resetError();
    }
  }, [pathname, resetError]);

  return <ErrorPage />;
};

export const RouteProviders = () => {
  const { isPublic } = useUrlChecker();

  return (
    <ErrorBoundary fallback={RouteErrorFallback}>
      <AnalyticsProvider>
        <HelmetProvider>
          <Helmet>
            <script
              type="text/javascript"
              id="hs-script-loader"
              async
              defer
              src={`//js.hs-scripts.com/${getEnv().hubspot_portal_id}.js`}
            />
            {isPublic && !location.pathname.includes('localhost') && (
              <script
                id="cookieyes"
                type="text/javascript"
                async
                src="https://cdn-cookieyes.com/client_data/f4c8903f602bc4de68ebe271/script.js"
              />
            )}
          </Helmet>
          <Outlet />
        </HelmetProvider>
      </AnalyticsProvider>
    </ErrorBoundary>
  );
};
